import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Circle as MapCircle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { PS, toneAccent } from '../theme';
import { Chip, MiniStat, VerifiedMark, DemoBadge } from '../lib/ui';
import { Icon } from '../lib/icons';
import { thb } from '../lib/format';
import MemberLayout from '../components/MemberLayout';
import { useFetch } from '../lib/useFetch';
import api from '../lib/api';
import { useAuth } from '../auth/AuthContext';

const pinIcon = (circle) => {
  const accent = circle.verified ? 'oklch(0.62 0.12 38)' : '#1a1a1a';
  return L.divIcon({
    className: 'ps-map-pin',
    html: `<div style="display:flex;flex-direction:column;align-items:center;transform:translateY(-4px)">
      <div style="padding:4px 9px;background:${accent};color:#fff;border-radius:9px;font-family:'IBM Plex Serif',serif;font-size:12px;font-weight:500;box-shadow:0 3px 8px rgba(0,0,0,0.25);white-space:nowrap">${thb(circle.amount_per_round)}</div>
      <div style="width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:7px solid ${accent};margin-top:-1px"></div>
    </div>`,
    iconSize: [60, 30], iconAnchor: [30, 30],
  });
};
const meIcon = L.divIcon({
  className: 'ps-me-pin',
  html: `<div style="position:relative"><div style="position:absolute;inset:-9px;background:rgba(32,28,24,0.16);border-radius:50%"></div><div style="width:14px;height:14px;border-radius:50%;background:#1a1a1a;border:3px solid #fbf8f2;box-shadow:0 2px 4px rgba(0,0,0,0.3)"></div></div>`,
  iconSize: [14, 14], iconAnchor: [7, 7],
});
// Anonymized status pin (private circles) — a BLURRED, non-clickable dot at an
// approximate location. Conveys "a real circle is somewhere around here" without
// revealing exact spot or letting you open it (details are members-only).
const statusPinIcon = (active) => {
  const c = active ? 'oklch(0.55 0.13 155)' : 'oklch(0.58 0.03 80)';
  return L.divIcon({
    className: 'ps-status-pin',
    html: `<div style="position:relative;width:40px;height:40px;pointer-events:none">
      <div style="position:absolute;inset:0;border-radius:50%;background:${c};opacity:0.16;filter:blur(7px)"></div>
      <div style="position:absolute;inset:13px;border-radius:50%;background:${c};opacity:0.9;filter:blur(1.2px);box-shadow:0 0 0 3px rgba(251,248,242,0.85)"></div>
    </div>`,
    iconSize: [40, 40], iconAnchor: [20, 20],
  });
};

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, map.getZoom(), { animate: true }); }, [center, map]);
  return null;
}

function DiscoverRow({ c, onClick }) {
  const accent = toneAccent(c.verified ? 'terra' : 'default');
  const full = c.open_slots === 0;
  return (
    <div data-testid={`discover-circle-${c.circle_id}`} onClick={onClick} className="ps-press ps-fade-up" style={{ margin: '0 16px 10px', background: PS.surface, borderRadius: 18, padding: 14, border: `1px solid ${PS.line}` }}>
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ width: 44, height: 44, borderRadius: 14, flexShrink: 0, background: accent, color: PS.surface2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: PS.fontNum, fontWeight: 500, fontSize: 18 }}>{(c.host_name || '?')[0]}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <div style={{ fontSize: 14.5, fontWeight: 600, letterSpacing: -0.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
              {c.is_demo && <DemoBadge />}
            </div>
            <div className="num" style={{ fontSize: 16, fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>{thb(c.amount_per_round)}</div>
          </div>
          <div style={{ fontSize: 11.5, color: PS.textMuted, marginTop: 2, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <Icon.pin size={12} stroke={PS.textMuted} />
            <span>{c.distance_km != null ? `${c.distance_km} กม.` : '—'}</span><span>·</span><span>{c.area_name}</span>
          </div>
          <div style={{ display: 'flex', gap: 14, marginTop: 10 }}>
            <MiniStat label="เครดิต" value={c.rating ? Math.round(700 + c.rating * 20) : '—'} />
            <MiniStat label="รีวิว" value={`${c.rating} (${c.reviews})`} />
            <MiniStat label="งวด" value={c.rounds_count} />
          </div>
          <div style={{ display: 'flex', gap: 5, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {c.verified ? <Chip tone="green"><VerifiedMark size={10} />ตรวจสอบ</Chip> : <Chip tone="outline">ยังไม่ตรวจสอบ</Chip>}
            {full ? <Chip tone="warn">เต็มแล้ว</Chip> : <Chip tone="terra">รับอีก {c.open_slots} คน</Chip>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Discover() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [radius, setRadius] = useState(20);
  const [q, setQ] = useState('');
  const [openOnly, setOpenOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [tag, setTag] = useState('');
  const [sort, setSort] = useState('distance');
  const [center, setCenter] = useState([user?.gps_lat || 13.837, user?.gps_lng || 100.545]);
  const [locating, setLocating] = useState(false);

  const qs = useMemo(() => {
    const p = new URLSearchParams({ max_km: String(radius), sort });
    if (q) p.set('q', q);
    if (tag) p.set('tag', tag);
    if (openOnly) p.set('open_only', 'true');
    if (verifiedOnly) p.set('verified_only', 'true');
    return p.toString();
  }, [radius, q, tag, openOnly, verifiedOnly, sort]);

  const { data: circles } = useFetch(`/circles?${qs}`, [qs]);
  const { data: tags } = useFetch('/discover/tags');
  const { data: statusPins } = useFetch('/circles/status-pins');
  const { data: stats } = useFetch('/stats/social-proof');

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      try { await api.patch('/me/location', { lat: latitude, lng: longitude }); await refresh?.(); } catch { /* noop */ }
      setCenter([latitude, longitude]);
      setLocating(false);
    }, () => setLocating(false), { enableHighAccuracy: true, timeout: 8000 });
  };

  const sorts = [['distance', 'ใกล้สุด'], ['open', 'รับเยอะสุด'], ['rating', 'เรตติ้ง']];

  return (
    <MemberLayout bg={PS.page}>
      <div style={{ position: 'relative', height: 300, overflow: 'hidden', flexShrink: 0 }}>
        <MapContainer center={center} zoom={12} style={{ height: '100%', width: '100%' }} zoomControl={false} attributionControl={false}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Recenter center={center} />
          <Marker position={center} icon={meIcon} />
          <MapCircle center={center} radius={radius * 1000} pathOptions={{ color: 'oklch(0.62 0.12 38)', fillColor: 'oklch(0.62 0.12 38)', fillOpacity: 0.08, weight: 1.5, dashArray: '6 6' }} />
          {(circles || []).filter(c => c.gps_lat).map(c => (
            <Marker key={c.circle_id} position={[c.gps_lat, c.gps_lng]} icon={pinIcon(c)}
              eventHandlers={{ click: () => nav(`/app/circle/${c.circle_id}`) }} />
          ))}
          {(statusPins || []).map((p, i) => (
            <React.Fragment key={`sp-${i}`}>
              <MapCircle center={[p.lat, p.lng]} radius={550}
                pathOptions={{ color: p.status === 'active' ? 'oklch(0.55 0.13 155)' : 'oklch(0.58 0.03 80)', fillColor: p.status === 'active' ? 'oklch(0.55 0.13 155)' : 'oklch(0.58 0.03 80)', fillOpacity: 0.06, weight: 1, opacity: 0.22, dashArray: '3 6', interactive: false }} />
              <Marker position={[p.lat, p.lng]} icon={statusPinIcon(p.status === 'active')}>
                <Popup>
                  <div style={{ fontFamily: PS.fontUI, fontSize: 12, lineHeight: 1.55, maxWidth: 196 }}>
                    <b>{p.status === 'active' ? '🔒 มีวงแชร์อยู่แถวนี้' : '🔒 เคยมีวงแชร์แถวนี้'}</b><br />
                    แสดง<b>ตำแหน่งโดยประมาณ</b>เพื่อความเป็นส่วนตัว · เป็นวงส่วนตัว <b>ดูรายละเอียดได้เฉพาะสมาชิกในวง</b> (เข้าผ่านลิงก์เชิญเท่านั้น)
                  </div>
                </Popup>
              </Marker>
            </React.Fragment>
          ))}
        </MapContainer>

        {(statusPins?.length > 0) && (
          <div style={{ position: 'absolute', top: 64, right: 14, background: 'rgba(251,248,242,0.96)', backdropFilter: 'blur(8px)', borderRadius: 12, padding: '7px 11px', fontSize: 10, fontFamily: PS.fontUI, color: PS.text, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 500, lineHeight: 1.7 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: 'oklch(0.62 0.12 38)', display: 'inline-block' }} />วงสาธารณะ (กดดูได้)</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: 'oklch(0.55 0.13 155)', display: 'inline-block', filter: 'blur(0.5px)' }} />🔒 วงส่วนตัว (ตำแหน่งโดยประมาณ)</div>
          </div>
        )}

        <div style={{ position: 'absolute', top: 14, left: 14, right: 14, display: 'flex', gap: 8, zIndex: 500 }}>
          <div style={{ flex: 1, height: 42, borderRadius: 21, background: 'rgba(251,248,242,0.96)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px', boxShadow: '0 6px 18px rgba(0,0,0,0.14)' }}>
            <Icon.search size={16} stroke={PS.textMuted} />
            <input data-testid="discover-search" value={q} onChange={e => setQ(e.target.value)} placeholder="ค้นหาวง / ย่าน / ชื่อท้าว" style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 13, outline: 'none', fontFamily: PS.fontUI, color: PS.text }} />
          </div>
          <button data-testid="discover-locate-btn" onClick={useMyLocation} className="ps-press" style={{ width: 42, height: 42, borderRadius: 21, background: PS.ink, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 18px rgba(0,0,0,0.18)' }}>
            <Icon.pin size={18} stroke={locating ? 'oklch(0.72 0.12 82)' : PS.page} />
          </button>
        </div>

        <div style={{ position: 'absolute', bottom: 14, left: 14, right: 14, background: 'rgba(251,248,242,0.96)', backdropFilter: 'blur(8px)', borderRadius: 16, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 6px 18px rgba(0,0,0,0.14)', zIndex: 500 }}>
          <Icon.walk size={16} stroke={PS.ink} />
          <input data-testid="radius-slider" type="range" min="1" max="50" value={radius} onChange={(e) => setRadius(Number(e.target.value))} style={{ flex: 1, accentColor: PS.ink }} />
          <div className="num" style={{ fontSize: 14, minWidth: 44, textAlign: 'right' }}>{radius} กม.</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 6, padding: '12px 16px 8px', overflowX: 'auto', flexShrink: 0 }}>
        <div data-testid="filter-open" onClick={() => setOpenOnly(v => !v)} className="ps-press" style={{ padding: '6px 12px', borderRadius: 999, flexShrink: 0, background: openOnly ? PS.terracotta : 'transparent', color: openOnly ? '#fff' : PS.text, border: openOnly ? 'none' : `1px solid ${PS.line}`, fontSize: 11.5, fontWeight: 600 }}>กำลังรับสมาชิก</div>
        <div data-testid="filter-verified" onClick={() => setVerifiedOnly(v => !v)} className="ps-press" style={{ padding: '6px 12px', borderRadius: 999, flexShrink: 0, background: verifiedOnly ? PS.ink : 'transparent', color: verifiedOnly ? PS.page : PS.text, border: verifiedOnly ? 'none' : `1px solid ${PS.line}`, fontSize: 11.5, fontWeight: 600 }}>✓ ตรวจสอบแล้ว</div>
        {(tags || []).slice(0, 6).map(t => (
          <div key={t.tag} data-testid={`filter-tag-${t.tag}`} onClick={() => setTag(tag === t.tag ? '' : t.tag)} className="ps-press" style={{ padding: '6px 12px', borderRadius: 999, flexShrink: 0, background: tag === t.tag ? PS.ink : 'transparent', color: tag === t.tag ? PS.page : PS.text, border: tag === t.tag ? 'none' : `1px solid ${PS.line}`, fontSize: 11.5, fontWeight: 500 }}>{t.tag}</div>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', background: PS.page }}>
        {stats?.beta_circles > 0 && (
          <div data-testid="track-record-badge" style={{ margin: '10px 16px 0', padding: '9px 13px', borderRadius: 12, background: PS.surface2, border: `1px solid ${PS.line}`, display: 'flex', alignItems: 'center', gap: 9, fontSize: 11.5, color: PS.text }}>
            <Icon.shield size={15} stroke={PS.ink} />
            <span>มีวงจริงใช้งานบนระบบแล้ว <b className="num">{stats.beta_circles}</b> วง{stats.active > 0 ? <> · กำลังทำงาน <b className="num">{stats.active}</b> วง</> : null}</span>
          </div>
        )}
        <div style={{ padding: '8px 20px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: -0.2 }}>วงใกล้คุณ</div>
            <div style={{ fontSize: 11.5, color: PS.textMuted, marginTop: 2 }}>{circles ? circles.length : 0} วงในรัศมี {radius} กม.</div>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {sorts.map(([v, l]) => (
              <button key={v} data-testid={`sort-${v}`} onClick={() => setSort(v)} className="ps-press" style={{ padding: '5px 10px', borderRadius: 8, border: 'none', background: sort === v ? PS.ink : 'transparent', color: sort === v ? PS.page : PS.textMuted, fontSize: 11, fontWeight: 600, fontFamily: PS.fontUI }}>{l}</button>
            ))}
          </div>
        </div>
        {(circles || []).map((c) => <DiscoverRow key={c.circle_id} c={c} onClick={() => nav(`/app/circle/${c.circle_id}`)} />)}
        {circles && circles.length === 0 && (
          <div style={{ padding: 30, textAlign: 'center', color: PS.textMuted, fontSize: 13, lineHeight: 1.7 }}>
            ไม่พบ<b>วงสาธารณะ</b>ในรัศมีนี้ — ลองขยายรัศมีหรือล้างตัวกรอง
            {statusPins?.length > 0 && <div style={{ marginTop: 6, fontSize: 11.5 }}>แต่มีวงส่วนตัวจริง <b className="num">{statusPins.length}</b> วงในแถบนี้ (จุดสีบนแผนที่) — วงส่วนตัวเข้าร่วมได้ผ่านลิงก์เชิญเท่านั้น</div>}
          </div>
        )}
        <div style={{ height: 20 }} />
      </div>
    </MemberLayout>
  );
}
